import { ROUTES, USER_ROLES } from './constants';
import { normalizeLoginRoleSelection } from '../auth/roleHeaders';

/**
 * Authenticated product portals. Login is a shared `/login` route with
 * `?mode=admin|user` plus a role tab for the user shell.
 */
export type AuthPortal = 'student' | 'teacher' | 'parent' | 'school-admin' | 'admin';

export const LAST_PORTAL_STORAGE_KEY = 'tkm_last_portal';
export const SELECTED_ROLE_STORAGE_KEY = 'tkm_selected_role';

const PORTAL_VALUES = new Set<string>([
  'student',
  'teacher',
  'parent',
  'school-admin',
  'admin',
]);

/** Login UI tab keys persisted in `tkm_selected_role`. */
const PORTAL_TO_LOGIN_TAB: Record<AuthPortal, string> = {
  student: 'student',
  teacher: 'teacher',
  parent: 'parent',
  'school-admin': 'school_admin',
  admin: 'dev_admin',
};

const ROLE_TO_PORTAL: Record<string, AuthPortal> = {
  [USER_ROLES.STUDENT]: 'student',
  [USER_ROLES.TEACHER]: 'teacher',
  [USER_ROLES.PARENT]: 'parent',
  [USER_ROLES.SCHOOL_ADMIN]: 'school-admin',
  [USER_ROLES.DEV_TEAM]: 'admin',
  [USER_ROLES.SUPPORT_AGENT]: 'admin',
  student: 'student',
  teacher: 'teacher',
  parent: 'parent',
  school_admin: 'school-admin',
  'school-admin': 'school-admin',
  dev_admin: 'admin',
  admin: 'admin',
};

function safeLocalStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore quota / private mode */
  }
}

export function isAuthPortal(value: string | null | undefined): value is AuthPortal {
  return !!value && PORTAL_VALUES.has(value);
}

export function resolvePortalFromRole(role: string | null | undefined): AuthPortal | null {
  if (!role) return null;
  const trimmed = role.trim();
  if (!trimmed) return null;

  const direct = ROLE_TO_PORTAL[trimmed] ?? ROLE_TO_PORTAL[trimmed.toUpperCase()];
  if (direct) return direct;

  const normalized = normalizeLoginRoleSelection(trimmed);
  if (normalized && ROLE_TO_PORTAL[normalized]) {
    return ROLE_TO_PORTAL[normalized];
  }

  return null;
}

/**
 * Infer portal from a protected app path (guards / logout from a role shell).
 */
export function resolvePortalFromPathname(pathname: string | null | undefined): AuthPortal | null {
  if (!pathname) return null;
  const path = pathname.split('?')[0] || '';

  if (path.startsWith('/student') || path.startsWith('/onboarding/student')) return 'student';
  if (path.startsWith('/teacher') || path.startsWith('/onboarding/teacher')) return 'teacher';
  if (path.startsWith('/parent')) return 'parent';
  if (path.startsWith('/admin') || path.startsWith('/onboarding/school')) return 'school-admin';
  if (path.startsWith('/dev')) return 'admin';

  return null;
}

export function rememberLastPortal(portal: AuthPortal): void {
  safeLocalStorageSet(LAST_PORTAL_STORAGE_KEY, portal);
}

export function getLastPortal(): AuthPortal | null {
  const raw = safeLocalStorageGet(LAST_PORTAL_STORAGE_KEY);
  return isAuthPortal(raw) ? raw : null;
}

/** Persist login-shell tab so `/login?mode=user` restores Student/Teacher/Parent. */
export function syncSelectedRoleFromPortal(portal: AuthPortal | null | undefined): void {
  if (!portal) return;
  safeLocalStorageSet(SELECTED_ROLE_STORAGE_KEY, PORTAL_TO_LOGIN_TAB[portal]);
}

/**
 * Resolve the active portal without clearing storage.
 * Priority: X_ROLE → selected role → pathname → lastPortal.
 */
export function resolveCurrentAuthPortal(options?: {
  pathname?: string | null;
  role?: string | null;
}): AuthPortal | null {
  const role =
    options?.role ??
    safeLocalStorageGet('X_ROLE') ??
    safeLocalStorageGet(SELECTED_ROLE_STORAGE_KEY);

  const fromRole = resolvePortalFromRole(role);
  if (fromRole) return fromRole;

  const pathname =
    options?.pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : null);
  const fromPath = resolvePortalFromPathname(pathname);
  if (fromPath) return fromPath;

  return getLastPortal();
}

/**
 * Guard redirects: prefer the protected portal the user tried to open.
 */
export function resolvePortalForGuard(pathname: string | null | undefined): AuthPortal | null {
  return (
    resolvePortalFromPathname(pathname) ??
    resolvePortalFromRole(safeLocalStorageGet('X_ROLE')) ??
    resolvePortalFromRole(safeLocalStorageGet(SELECTED_ROLE_STORAGE_KEY)) ??
    getLastPortal()
  );
}

export function getLoginModeForPortal(portal: AuthPortal | null | undefined): 'admin' | 'user' {
  if (portal === 'school-admin' || portal === 'admin') return 'admin';
  return 'user';
}

export function appendLoginSearchParams(
  loginPath: string,
  params: Record<string, string | null | undefined>
): string {
  const qIndex = loginPath.indexOf('?');
  const path = qIndex >= 0 ? loginPath.slice(0, qIndex) : loginPath;
  const existing = qIndex >= 0 ? loginPath.slice(qIndex + 1) : '';
  const sp = new URLSearchParams(existing);

  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === '') {
      sp.delete(key);
    } else {
      sp.set(key, value);
    }
  }

  const query = sp.toString();
  return query ? `${path}?${query}` : path;
}

/**
 * Build the correct login URL for a portal.
 * App uses shared `/login` with `mode=admin|user` (not per-role path routes).
 */
export function getLoginPathForPortal(
  portal: AuthPortal | null | undefined,
  options?: { returnTo?: string | null }
): string {
  const mode = getLoginModeForPortal(portal);
  return appendLoginSearchParams(ROUTES.LOGIN, {
    mode,
    returnTo: options?.returnTo ?? null,
  });
}

/**
 * Resolve login destination for logout / session expiry / 401.
 * Call BEFORE clearing auth storage so role can still be read.
 */
export function getLoginPathForCurrentSession(options?: {
  pathname?: string | null;
  returnTo?: string | null;
  role?: string | null;
}): string {
  const portal = resolveCurrentAuthPortal({
    pathname: options?.pathname,
    role: options?.role,
  });
  return getLoginPathForPortal(portal, { returnTo: options?.returnTo });
}

/**
 * Capture portal for post-clear redirects, then sync login tab selection.
 * Safe to call immediately before `clearAuthSessionStorage()`.
 */
export function preservePortalForLogout(options?: {
  pathname?: string | null;
  role?: string | null;
}): AuthPortal | null {
  const portal = resolveCurrentAuthPortal(options);
  if (portal) {
    rememberLastPortal(portal);
    syncSelectedRoleFromPortal(portal);
  }
  return portal;
}

/** Call after successful login so later expiry still knows the shell. */
export function rememberPortalFromLoginRole(role: string | null | undefined): void {
  const portal = resolvePortalFromRole(role);
  if (!portal) return;
  rememberLastPortal(portal);
  syncSelectedRoleFromPortal(portal);
}
